import { Button } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import { download, setAutoDownloadFlag } from '../../utility/api';
import { useEffect } from 'react';
import { isMobileDevice } from '../../utility/utility';
import { usePavilion } from '../contexts/PavilionContext';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import config from '../../config.json';

export const FAQs = () => {
  const { isPavilionMode } = usePavilion();

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return isPavilionMode ? (
    <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
      <div
        className='bg-herogibber bg-content text-content-text m-0 h-auto w-full bg-cover bg-no-repeat p-0'
        style={{ backgroundColor: '#2C354C' }}
      >
        <div className='container mx-auto h-auto w-full px-8 pb-16 pt-16'>
          <section id='hero' className='grid grid-cols-12 gap-x-4 gap-y-8'>
            <div className='grid-rows col-span-full grid content-center gap-y-4 md:col-span-6 lg:gap-y-8'>
              <h1 className='row-auto text-3xl font-bold lg:text-4xl'>Frequently Asked Questions</h1>
              <p className='row-auto text-lg'>Providing answers to questions we hear often!</p>
            </div>
            <div className='col-span-full flex flex-col items-center justify-center pr-20 md:col-span-5 md:col-start-8'>
              <img src='/GradientLogo.svg' className='h-[343px] w-[346px]' alt='gradientlogo' />
              <img src='/PlaycastPavilionText.svg' className='inline-block h-[166px] w-[461px] object-contain' alt='Playcast Pavilion' />
            </div>
          </section>
        </div>
      </div>

      <div className='bg-content-secondary text-content-text m-0 h-auto w-full'>
        <div className='container mx-auto h-auto w-full px-0 py-20'>
          {/* grid grid-cols-12 gap-10 md:gap-8 items-start */}
          <div className='grid grid-cols-1 gap-8 px-4 md:grid-cols-2'>
            {/* col-span-full md:col-span-6 grid grid-rows gap-y-4  items-start */}
            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>General Questions</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What is Playcast Pavilion?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast Pavilion is a Steam-integrated Windows app that transforms any gaming PC into a live-streaming server, a
                  "Pavilion Kiosk". It powers a global, low-latency marketplace where developers, streamers, hosts, and players connect to
                  share, showcase, and experience games—instantly and interactively.
                  <br />
                  <br />
                  For developers, it's a way to promote games by licensing them for public performance through Steam Café or promotional
                  keys. For players, it offers instant access to top PC games without installs or expensive hardware. For hosts, it's a way
                  to earn free games by donating idle PC time to support the network.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  What's the difference between Playcast Pavilion and the standard Playcast app?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast Pavilion is a commercial, Steam-integrated service that supports public performance licensing and rewards Hosts.
                  The standard Playcast app, at Playcast.io, is a free, non-monetized platform for sharing personal games with friends and
                  family.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Is Playcast Pavilion free to use?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! It's completely free for players. No gaming PC, accounts, or game ownership is required.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Who is Playcast Pavilion for?</summary>
                <ul className='row-auto list-disc space-y-2 px-16 pb-8 pt-2'>
                  <li>
                    <a
                      href='https://pavilion.playcast.io'
                      className='text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Streamers
                    </a>
                  </li>
                  <li>
                    <a
                      href='https://pavilion.playcast.io'
                      className='text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Game Developers
                    </a>
                  </li>
                  <li>
                    <a
                      href='https://pavilion.playcast.io'
                      className='text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      PC Gamers
                    </a>
                  </li>
                  <li>
                    <a
                      href='https://pavilion.playcast.io'
                      className='text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Hosts
                    </a>
                  </li>
                </ul>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>For Streamers</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What can I do with Playcast Pavilion as a streamer?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <p>You can:</p>
                  <ul className='list-disc space-y-2 pl-6'>
                    <li>Play games you don't own</li>
                    <li>Invite viewers to join your game lobbies</li>
                    <li>Run sponsored events, giveaways, and raffles</li>
                  </ul>
                  <p>
                    <a
                      href='https://docs.google.com/forms/d/e/1FAIpQLSeA8L9u_eaP6QT0eQCJnNiwMOZysKc3t5pwwk8DhStY-JseaA/viewform'
                      className='font-medium text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Join the Playcast Streamer community
                    </a>
                  </p>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Can my audience donate their PCs to help me host?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! Fans can designate their PCs as Hosts for your channel, increasing your streaming power and reach.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>For Game Developers</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>How do I get my game on Playcast Pavilion?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <ol className='list-decimal space-y-2 pl-6'>
                    <li>
                      <a
                        href='https://partner.steamgames.com/pccafe'
                        className='text-blue-500 hover:underline'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Register your title through the Steam Café
                      </a>
                      .
                    </li>
                    <li>
                      <a
                        href='https://docs.google.com/forms/d/e/1FAIpQLScFG-0N0DjPwt1n0n_skBPu1C3hHaQ1SKP3To4P1mrIAv2hmg/viewform'
                        className='text-blue-500 hover:underline'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Notify the Playcast Team
                      </a>
                      .
                    </li>
                    <li>We'll onboard your game and seed it across Pavilion Kiosks.</li>
                  </ol>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What licensing options do I have?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <p>You can choose to:</p>
                  <ul className='list-disc space-y-2 pl-6'>
                    <li>Distribute promo keys for limited access</li>
                    <li>Sell public performance licenses through Steam Café</li>
                    <li>Sell retail copies as usual on Steam</li>
                  </ul>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Will Playcast buy licenses for my game?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! We purchase a minimum of 5 Steam Café licenses for every qualifying title to support initial distribution.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What are the benefits?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <ul className='list-disc space-y-2 pl-6'>
                    <li>Real-time gameplay exposure</li>
                    <li>Frictionless trial access for players</li>
                    <li>Embedded buy links to drive Steam sales</li>
                  </ul>
                </div>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>For Players</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Do I need to own the games or have a gaming PC?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <p>Nope! Just pick a Pavilion Kiosk and stream the game directly in your browser on almost any device.</p>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>How do I start playing?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <ol className='list-decimal space-y-2 pl-6'>
                    <li>
                      Go to <span className='italic'>&quot;default Playcast profile page&quot;</span>
                    </li>
                    <li>Press the 'Create Lobby' button</li>
                    <li>Invite friends</li>
                    <li>Play!</li>
                  </ol>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Are there any fees?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  None. Playing on Playcast Pavilion is completely free for gamers. You may even earn Steam gift cards during promotional
                  events.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Can I earn anything as a player?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! Occasionally we offer incentives, like Steam gift cards, for new players who complete their first game lobby experience. Watch for
                  announcements on affiliate promotions.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>For Hosts</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What is a Playcast Pavilion Host?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <p>
                    A Host is anyone who volunteers their gaming PC to the Pavilion network in exchange for free games, gift cards, and
                    rewards.
                  </p>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What is a Playcast Pavilion Kiosk?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  A "Kiosk" is a secure, sandboxed PC environment that allows remote players to stream licensed games safely.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Is my PC safe?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes. Players only stream the games you authorize. They never access your desktop or files
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Do I need to leave my PC on 24/7?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Nope—you can schedule host times or enable async hosting during idle hours
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Why can't I stream any Steam game with Playcast Pavilion?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Only games with Steam Café licenses or developer approval can be hosted in Playcast Pavilion. This ensures full compliance
                  with Steam's public performance policies.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Do I get access to free games as a Host?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! Hosts receive access to a licensed catalog of games in exchange for contributing idle PC time to the network.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Still have questions?</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <p>
                    Email us at{' '}
                    <a href='mailto:support@playcast.io' className='text-blue-500 hover:underline'>
                      support@playcast.io
                    </a>{' '}
                    or join our{' '}
                    <a
                      href={config.DISCORD_URL}
                      className='text-blue-500 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Discord
                    </a>{' '}
                    to chat with the team.
                  </p>
                </div>
              </details>
            </section>

            <section id='how-to-set-up-bots' className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>How to Set Up Bots</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Discord Bots</summary>
                <div className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <ol className='list-decimal space-y-2 pl-6'>
                    <li>Link Discord to your Playcast account</li>
                    <li>Click the Invite Discord Bot Button (If you have not Linked Discord the button will be greyed out)</li>
                    <li>Select the Server you want the Bot in!</li>
                    <li>Allow the Web Hooks and Audit Log</li>
                    <li>There will be a Direct Message sent to you that has a command that will help you get started!</li>
                    <li>Just in case you dont get the message! The command is /pc-announce</li>
                  </ol>
                </div>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Twitch Bots</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  <ol className='list-decimal space-y-2 pl-6'>
                    <li>Link your Twitch account to your Playcast account!</li>
                    <li>Click the Invite Twitch Bot Button (If you have not Linked Twitch the button will be greyed out)</li>
                    <li>Go to your Twitch channel community</li>
                    <li>In a few seconds to a couple minutes a Playcast Bot should appear in the list!</li>
                    <li>Make them a MOD for them to send messages!</li>
                  </ol>
                </p>
              </details>
            </section>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
      <div className='bg-hero bg-content text-content-text m-0 h-auto w-full bg-cover bg-no-repeat p-0'>
        <div className='container mx-auto h-auto w-full px-8 pb-8 pt-20'>
          <section id='hero' className='grid grid-cols-12 gap-x-4 gap-y-8'>
            <div className='grid-rows col-span-full grid content-center gap-y-4 md:col-span-6 lg:gap-y-8'>
              <h1 className='row-auto text-3xl font-bold lg:text-4xl'>Frequently Asked Questions</h1>
              <p className='row-auto text-lg'>Providing answers to questions we hear often!</p>
              <p className='row-auto text-lg'>Looking for Playcast? Download it here!</p>
              <div className='row-auto flex flex-row gap-2'>
                {!isMobileDevice() && (
                  <Button className='flex space-x-2 rounded-full bg-amber-400' onClick={() => download()}>
                    <FontAwesomeIcon icon={faWindows} className='text-2xl' />
                    <span>Download</span>
                  </Button>
                )}
              </div>
            </div>

            <div className='col-span-full content-center md:col-span-5 md:col-start-8'>
              <img src='/images/preview-image.png' alt='screenshot' />
            </div>
          </section>
        </div>
      </div>

      <div className='bg-content-secondary text-content-text m-0 h-auto w-full'>
        <div className='container mx-auto h-auto w-full px-0 py-20'>
          {/* grid grid-cols-12 gap-10 md:gap-8 items-start */}
          <div className='grid grid-cols-1 gap-8 px-4 md:grid-cols-2'>
            {/* col-span-full md:col-span-6 grid grid-rows gap-y-4  items-start */}
            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>General Questions</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What is Playcast?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast is a brand-new way to share and play games online with friends. It enables you to host your games with full
                  interactivity from your home PC to anyone you wish to play with. With Playcast, friends can seamlessly play games on your
                  computer from a remote location, as if they were physically present beside you, and you can do the same with their games!
                </p>
                <p className='row-auto space-y-4 px-16 pb-8'>
                  Simply download the Playcast App, start a session, and share the invite link with whomever you want to join. They'll be
                  able to play from any device without having to download anything or make an account.
                </p>
                <p className='row-auto space-y-4 px-16 pb-8'>
                  Additionally, hosts can stream fully interactive games publicly by integrating with Open Broadcaster Software (OBS) for
                  streaming to platforms like Twitch and YouTube.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>How does Playcast work?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Install the Playcast App on your home PC, and make your entire game library shareable. Launch a session, and send an
                  invite link to your friends, enabling them to join your gaming session and play games using their own mouse, keyboard,
                  gamepad, or touchscreen from any browser or device.
                </p>
                <p className='row-auto space-y-4 px-16 pb-8'>
                  The Playcast App operates similarly to Open Broadcaster Software (OBS), but instead of merely capturing gameplay video and
                  sending it to a video service like Twitch, it directly streams the video to any number of player browsers. It also
                  captures their player inputs and sends them back to your Playcast App, as if their remote input devices were physically
                  connected to your local PC.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Do I need to download any software to use Playcast?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  If you'd like to host your games for others to play, download the Playcast App from the top of this page. Participants who
                  join do not need to download anything.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What devices can I use with Playcast?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  The Playcast App runs on Windows PCs. Participants joining a hosted session can play from any device.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Is Playcast free to use?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast is completely free for hosting and joining play sessions. Premium offerings for professional streamers and game
                  publishers may be available in the future.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>How do I invite friends to play games with me on Playcast?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Invite friends to play games with you by sharing an invite URL generated by the Playcast App.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Where can I find help if I encounter any issues while using Playcast?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  If you encounter any issues while using Playcast, you can refer to our <a href='https://discord.gg/7PYGxU68WH'>Discord</a>{' '}
                  for troubleshooting guides and to get support for further assistance.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>Technical Questions</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Is Playcast secure?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  The Playcast App is a powerful remote desktop sharing application. Hosts can grant themselves and trusted friends and
                  family complete remote access to their PC, and hosts should be judicious with whom they grant this access to. To ensure
                  optimally secure interactions, please make sure to only capture and send video of relevant sources (like the game you're
                  hosting). Additionally, we recommend limiting input access for participants to gamepad only.
                </p>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast takes security and privacy seriously and is developing additional measures to ensure that gameplay sessions are
                  secure and protected. Playcast invitation links are encrypted and can't be discovered by anyone you don't share them with.
                  Playcast hosts are also required to 2FA authenticate their servers if they are sharing access to a Windows Admin account
                  to ensure that only trusted users can share access to a PC online.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>What happens if there's lag during a game session?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast strives to minimize lag during gameplay sessions, but if lag occurs, you can try troubleshooting your internet
                  connection or adjusting your settings for optimal performance.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  How do I know if my internet connection is suitable for using Playcast?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast requires a stable internet connection with sufficient upload bandwidth to stream games smoothly. You can check
                  your internet speed and latency using online tools to ensure optimal performance. Generally 20Mb/s of upload bandwidth is
                  sufficient for a high quality gaming experience for 1-3 additional player desktop gaming sessions or up to 8 mobile
                  players.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Is there a limit to the number of players in a game session?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Most Playcast host users have enough upload bandwidth for Playcast live gameplay with 3-4 friends. Additional spectators
                  can also join to watch and play at the host's discretion.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl font-bold text-amber-400'>Compatibility & Features</h2>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Can I use Playcast and stream my gameplay to platforms like Twitch or YouTube?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes, you can stream your Playcast sessions to platforms like Twitch or YouTube, allowing you to get your viewers involved
                  and share your gaming experiences with a wider audience.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Can I use Playcast to play games offline?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  No, Playcast requires an internet connection to stream games and interact with other players in real-time.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Can I use Playcast to play games with friends who are located in different countries?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! Playcast allows players to connect and play games with friends from anywhere in the world, as long as all
                  participants have a stable internet connection. Some games may be unsuitable for streaming over extremely large distances
                  due to latency.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Does Playcast support local multiplayer games, such as split-screen and couch co-op?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes! Playcast turns all local split-screen and couch co-op style games into online multiplayer games. Each remote player
                  will need their own device to join a game.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Can I use Playcast to share my gameplay with others who don't own the game?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes, Playcast allows you to share your gameplay with friends who don't own the game or gaming devices that support the
                  game. No downloading, installing or registration is required for those participating in your session.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>Is Playcast compatible with virtual reality (VR) gaming?</summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  While Playcast does not currently support VR gaming directly, you can still stream non-VR games to VR headsets and play
                  them in a virtual environment.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Are there any restrictions on the types of games I can play on Playcast?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Playcast can stream any Windows game, application, or media. Hosts are responsible for conforming to any licensing
                  restrictions that may be associated with the software and media that they own.
                </p>
              </details>

              <details className='bg-content row-auto rounded-lg border border-gray-600'>
                <summary className='row-auto px-6 py-5 text-lg italic'>
                  Can I use Playcast to host private gaming sessions with a select group of friends?
                </summary>
                <p className='row-auto space-y-4 px-16 pb-8 pt-2'>
                  Yes, there is no limit to the number of players or spectators you can invite to a Playcast session but your home upload
                  bandwidth will generally limit the maximum number of viewers to 8-12. Beyond that, Open Broadcaster Software (OBS) or
                  Discord can be used to stream to larger groups.
                </p>
              </details>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
